/**
 * Classification d'erreurs pour mode dégradé intelligent
 * Permet de différencier les types d'erreurs et adapter la réponse
 */

export enum ErrorSeverity {
  /** Erreur mineure - peut continuer avec fallback */
  LOW = 'low',
  /** Erreur moyenne - fonctionnalité réduite */
  MEDIUM = 'medium',
  /** Erreur haute - blocage partiel */
  HIGH = 'high',
  /** Erreur critique - blocage total */
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  /** Problème réseau/connectivité */
  NETWORK = 'network',
  /** Erreur d'authentification */
  AUTH = 'auth',
  /** Erreur base de données */
  DATABASE = 'database',
  /** Erreur de validation */
  VALIDATION = 'validation',
  /** Erreur métier/logique */
  BUSINESS = 'business',
  /** Erreur externe (API tiers) */
  EXTERNAL = 'external',
  /** Erreur inconnue */
  UNKNOWN = 'unknown'
}

export interface ClassifiedError {
  original: Error;
  category: ErrorCategory;
  severity: ErrorSeverity;
  isRetryable: boolean;
  userMessage: string;
  technicalMessage: string;
  suggestedAction: 'retry' | 'refresh' | 'logout' | 'contact_support' | 'wait';
  retryAfterMs?: number;
}

/**
 * Patterns de détection d'erreurs réseau
 */
const NETWORK_ERROR_PATTERNS = [
  'fetch failed',
  'network error',
  'net::',
  'failed to fetch',
  'networkerror',
  'connection refused',
  'timeout',
  'econnrefused',
  'enotfound',
  'socket hang up',
  'CORS',
  'cross-origin'
];

/**
 * Patterns de détection d'erreurs d'authentification
 */
const AUTH_ERROR_PATTERNS = [
  'jwt',
  'token',
  'unauthorized',
  '401',
  'session',
  'auth',
  'invalid credentials',
  'not authenticated',
  'expired'
];

/**
 * Patterns de détection d'erreurs base de données
 */
const DB_ERROR_PATTERNS = [
  'pgrst',
  'postgres',
  'duplicate key',
  'foreign key',
  'constraint',
  'rls',
  'permission denied',
  '42501',
  '23505',
  '23503'
];

/**
 * Patterns de détection d'erreurs de rate limiting
 */
const RATE_LIMIT_PATTERNS = [
  '429',
  'rate limit',
  'too many requests',
  'quota exceeded'
];

/**
 * Classifie une erreur pour déterminer la meilleure stratégie de récupération
 */
export function classifyError(error: Error | unknown): ClassifiedError {
  const err = error instanceof Error ? error : new Error(String(error));
  const message = err.message.toLowerCase();
  const name = err.name.toLowerCase();
  const combined = `${name} ${message}`;

  // Détection réseau
  if (NETWORK_ERROR_PATTERNS.some(p => combined.includes(p.toLowerCase()))) {
    return {
      original: err,
      category: ErrorCategory.NETWORK,
      severity: ErrorSeverity.MEDIUM,
      isRetryable: true,
      userMessage: 'Problème de connexion. Vérifiez votre connexion internet.',
      technicalMessage: err.message,
      suggestedAction: 'retry',
      retryAfterMs: 3000
    };
  }

  // Détection rate limiting
  if (RATE_LIMIT_PATTERNS.some(p => combined.includes(p.toLowerCase()))) {
    return {
      original: err,
      category: ErrorCategory.EXTERNAL,
      severity: ErrorSeverity.LOW,
      isRetryable: true,
      userMessage: 'Service temporairement surchargé. Réessayez dans quelques secondes.',
      technicalMessage: err.message,
      suggestedAction: 'wait',
      retryAfterMs: 10000
    };
  }

  // Détection authentification
  if (AUTH_ERROR_PATTERNS.some(p => combined.includes(p.toLowerCase()))) {
    return {
      original: err,
      category: ErrorCategory.AUTH,
      severity: ErrorSeverity.HIGH,
      isRetryable: false,
      userMessage: 'Votre session a expiré. Veuillez vous reconnecter.',
      technicalMessage: err.message,
      suggestedAction: 'logout'
    };
  }

  // Détection base de données
  if (DB_ERROR_PATTERNS.some(p => combined.includes(p.toLowerCase()))) {
    const isPermission = combined.includes('permission') || combined.includes('42501');
    return {
      original: err,
      category: ErrorCategory.DATABASE,
      severity: isPermission ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM,
      isRetryable: !isPermission,
      userMessage: isPermission 
        ? 'Vous n\'avez pas les droits nécessaires pour cette action.'
        : 'Erreur lors de l\'enregistrement des données.',
      technicalMessage: err.message,
      suggestedAction: isPermission ? 'contact_support' : 'retry',
      retryAfterMs: isPermission ? undefined : 2000
    };
  }

  // Erreur inconnue
  return {
    original: err,
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    isRetryable: true,
    userMessage: 'Une erreur inattendue s\'est produite.',
    technicalMessage: err.message,
    suggestedAction: 'retry',
    retryAfterMs: 2000
  };
}

/**
 * Vérifie si une erreur doit déclencher le mode dégradé
 */
export function shouldEnterDegradedMode(error: ClassifiedError): boolean {
  return (
    error.severity === ErrorSeverity.HIGH || 
    error.severity === ErrorSeverity.CRITICAL ||
    error.category === ErrorCategory.NETWORK
  );
}

/**
 * Génère un ID unique pour tracker une erreur
 */
export function generateErrorId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
